import { useTranslation } from 'react-i18next';
import InteractiveCard from '../../common/interactiveCard';
import { useEffect, useState } from 'react';
import { Edit, ArrowRight, User, StickyNote, Calendar, Delete } from '../../icons';
import CardData from '../cardData';
import NoteAttachment from './noteAttachment';
import { INote } from '../../../interface/notes';
import { ROUTES } from '../../../constants/constants';
import { useFetchNotesById } from '../../../hooks/useFetchNotesDetail';
import { useLocation, useParams } from 'react-router-dom';
import { usePage } from '../../../context/PageContext';
import { useDeleteNote } from '../../../hooks/useDeleteNote';
import ConfirmModal from '../../confirmationmodal/confirmationModal';
import {
  borderColorMapping,
  SignificanceTypeValue,
  tagColorMapping,
} from '../../../constants/notes';
import { Divider, IconButton, Tooltip } from '../../Ant';
import { Flex } from 'antd';
import AddEditTransactionHeader from '../addTransaction/addEditTransactionHeader';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { TransactionViewTab } from '../../../constants/transaction';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { AnalyticsPage } from '../../../interface/analytics';
import { getLocalDateAndTime } from '../../../constants/utilityFunctions';
import SkeletonEntityCard from '../../common/skeleton/skeletonEntityCard';

const NotesCard = ({
  detailedView = false,
  note: noteDetail,
  entitySubject,
}: {
  detailedView?: boolean;
  note?: INote;
  entitySubject?: string;
}) => {
  const { notesId } = useParams();
  const { t } = useTranslation();
  const location = useLocation();
  const { goBack, push } = useNavigationStack();
  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const deleteNoteMutation = useDeleteNote();
  const { data, refetch, isLoading } = useFetchNotesById({
    id: notesId,
    relations: ['createdBy:fullName'],
    fields: [
      'note',
      'significance',
      'createdOn',
      'modifiedOn',
      'containsAttachment',
      'attachmentFileName',
      'attachmentFileData',
      'attachmentFileSize',
      'attachmentMimeType',
    ],
  });
  const [note, setNote] = useState<INote | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  useEffect(() => {
    if (detailedView) trackPage(AnalyticsPage.NOTES_DETAIL);
  }, [trackPage]);
  const handleBack = () => {
    goBack({ fromTab: TransactionViewTab.notes });
  };
  const deleteNote = () => {
    deleteNoteMutation.mutate(notesId ?? '', {
      onSettled() {
        setShowDeleteConfirm(false);
        handleBack();
      },
    });
  };
  const handleConfirmDelete = () => {
    setShowDeleteConfirm(true);
  };
  const renderHeader = () => {
    return (
      <AddEditTransactionHeader
        handleBack={handleBack}
        subTitle={t('note:note')}
        handleDelete={handleConfirmDelete}
        title={location?.state?.subject ?? ''}
      />
    );
  };
  useEffect(() => {
    if (noteDetail) {
      setNote(noteDetail);
    }
    if (data && !isLoading) {
      setNote(data);
      setTitle(renderHeader());
    }
  }, [noteDetail, data, isLoading]);

  const handleShowDetail = () => {
    push(`${ROUTES.NOTES_VIEW}/${note?.id}`, {
      subject: entitySubject,
    });
  };
  const handleEditNote = () => {
    push(`${ROUTES.ADD_EDIT_NOTES}/${note?.id}`);
  };
  if (isLoading) {
    return <SkeletonEntityCard rows={[1, 2]} />;
  }
  return (
    <>
      <InteractiveCard
        $isDetail={detailedView}
        className={`primary-card  ${detailedView ? 'detailed-view' : 'pointer'} ${borderColorMapping[note?.significance as SignificanceTypeValue]}`}
        onActivate={handleShowDetail}
      >
        <Flex justify="space-between" align="center" className="pb-10">
          <Flex
            justify="center"
            align="center"
            className={`status-chip d-flex justify-center items-center ${tagColorMapping[note?.significance as SignificanceTypeValue]}`}
          >
            {t(
              `note:significance.${Object.keys(SignificanceTypeValue).find((key) => SignificanceTypeValue[key as keyof typeof SignificanceTypeValue] === note?.significance) ?? note?.significance}`,
            )}
          </Flex>
          <Flex gap={10}>
            <Tooltip title={t('common:edit')} placement="top" arrow={false}>
              <IconButton
                icon={<Edit />}
                aria-label={t('common:edit')}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditNote();
                }}
              />
            </Tooltip>

            {!detailedView && (
              <Tooltip title={t('transaction:goDetail')} placement="top" arrow={false}>
                <IconButton
                  icon={<ArrowRight />}
                  aria-label={t('transaction:goDetail')}
                  data-testid="notes-arrow-button"
                />
              </Tooltip>
            )}
          </Flex>
        </Flex>
        <Divider className={`${detailedView ? 'mb-0' : 'mb-10'}`} />
        <Flex vertical gap={10} className="detail-note-container">
          {detailedView ? (
            <div className="notes-scrollable">
              <CardData
                className="notes-data pt-10"
                icon={<StickyNote />}
                title={t('note:note')}
                subcontent={note?.note ?? ''}
                subcontentclass="fw-400 fz-13 d-block lh-20 text-clr-general-description"
                disableTooltip
              />
            </div>
          ) : (
            <CardData
              className="notes-data"
              icon={<StickyNote />}
              title={t('note:note')}
              subcontent={note?.note ?? ''}
              subcontentclass="fw-400 lineclamp-1"
            />
          )}
          <div className="d-flex justify-between">
            <CardData
              icon={<User />}
              title={t('note:createdBy')}
              content={note?.createdBy?.fullName ?? ''}
            />
            <CardData
              icon={<Calendar />}
              title={t('note:modifiedOn')}
              type="modifiedOn"
              content={getLocalDateAndTime(new Date(note?.modifiedOn ?? ''), true)}
            />
          </div>
          {note?.containsAttachment && (
            <Flex gap={10}>
              <NoteAttachment note={note} detailedView={detailedView} refetch={refetch} />
            </Flex>
          )}
        </Flex>
      </InteractiveCard>
      <ConfirmModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        icon={<Delete />}
        title={t('note:deleteNoteQuestion')}
        subtitle={t('note:deleteNoteText')}
        cancelText={t('common:cancel')}
        confirmText={t('note:delete')}
        onConfirm={deleteNote}
      />
    </>
  );
};

export default NotesCard;
